import { GameAnalytics as GA } from "gameanalytics";

// Enum values from gameanalytics SDK
const EGAProgressionStatus = {
    Start: 1,
    Complete: 2,
    Fail: 3
} as const;

const EGAResourceFlowType = {
    Source: 1,
    Sink: 2
} as const;

const EGAErrorSeverity = {
    Debug: 1,
    Info: 2,
    Warning: 3,
    Error: 4,
    Critical: 5
} as const;

export class GameAnalytics {
    private static initialized = false;

    public static init(): void {
        if (this.initialized) return;

        // @ts-ignore - ts-jest compiles with CJS modules in tests
        const gameKey = import.meta.env.VITE_GAMEANALYTICS_GAME_KEY;
        // @ts-ignore - ts-jest compiles with CJS modules in tests
        const secretKey = import.meta.env.VITE_GAMEANALYTICS_SECRET_KEY;

        if (!gameKey || !secretKey) {
            console.warn("GameAnalytics keys not found in environment variables.");
            return;
        }

        GA.configureBuild("1.0.0");

        // @ts-ignore
        if (window.App && window.App.user_id) {
            // @ts-ignore
            GA.configureUserId(String(window.App.user_id));
        }

        GA.configureAvailableResourceCurrencies([
            "Money",
            "Gem",
            "FurCoin",
            "Spins",
            "BonusSpins"
        ]);

        GA.configureAvailableResourceItemTypes([
            "ContractReward",
            "Shop",
            "Breeding",
            "Gameplay",
            "AchievementReward",
            "InviteReward",
            "LevelUpReward",
            "MutationScratchReward",
            "GameOfRiskReward",
            "PepeReward",
            "SlotMachine"
        ]);

        GA.initialize(gameKey, secretKey);
        this.initialized = true;
    }

    public static trackDesignEvent(eventId: string, value?: number): void {
        if (!this.initialized) return;
        if (value !== undefined) {
            GA.addDesignEvent(eventId, value);
        } else {
            GA.addDesignEvent(eventId);
        }
    }

    public static trackProgressionEvent(
        status: number,
        progression01: string,
        progression02?: string,
        progression03?: string,
        score?: number
    ): void {
        if (!this.initialized) return;
        GA.addProgressionEvent(status, progression01, progression02, progression03, score);
    }

    public static trackResourceEvent(
        flowType: number,
        currency: string,
        amount: number,
        itemType: string,
        itemId: string
    ): void {
        if (!this.initialized) return;
        GA.addResourceEvent(flowType, currency, amount, itemType, itemId);
    }

    public static trackErrorEvent(severity: number, message: string): void {
        if (!this.initialized) return;
        GA.addErrorEvent(severity, message);
    }

    public static trackProgressionStart(category: string, step: string): void {
        if (!this.initialized) return;
        GA.addProgressionEvent(
            EGAProgressionStatus.Start,
            category,
            step
        );
    }

    public static trackProgressionComplete(category: string, step: string, score?: number): void {
        if (!this.initialized) return;
        GA.addProgressionEvent(
            EGAProgressionStatus.Complete,
            category,
            step,
            undefined,
            score
        );
    }

    public static trackResourceGain(currency: string, amount: number, source: string, itemId: string): void {
        if (!this.initialized) return;
        GA.addResourceEvent(
            EGAResourceFlowType.Source,
            currency,
            amount,
            source,
            itemId
        );
    }

    public static trackResourceSpend(currency: string, amount: number, sink: string, itemId: string): void {
        if (!this.initialized) return;
        GA.addResourceEvent(
            EGAResourceFlowType.Sink,
            currency,
            amount,
            sink,
            itemId
        );
    }
}
